import { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowRight, ArrowLeft, Drop, Truck, Wrench, ShieldCheck, Lightning, Target, HardHat, CaretRight } from "@phosphor-icons/react";

const fadeUp = { hidden: { opacity: 0, y: 40 }, visible: { opacity: 1, y: 0, transition: { duration: 0.6 } } };
const fadeLeft = { hidden: { opacity: 0, x: -40 }, visible: { opacity: 1, x: 0, transition: { duration: 0.6 } } };
const fadeRight = { hidden: { opacity: 0, x: 40 }, visible: { opacity: 1, x: 0, transition: { duration: 0.6 } } };
const stagger = { visible: { transition: { staggerChildren: 0.15 } } };

const slides = [
  {
    image: "/images/bitumen-drums-multiple.jpeg",
    overline: "Bitumen & Emulsion Supply",
    title: "Premium Bitumen Solutions for Southern Africa",
    subtitle: "Comprehensive range of emulsions, primes, pre-coats and penetration grade bitumen meeting SANRAL & international standards.",
    cta: { label: "View Bitumen Products", to: "/products/bitumen-solutions" },
  },
  {
    image: "/images/mobile-crusher-hero.jpg",
    overline: "Authorized FTM Distributor",
    title: "World-Class Heavy Machinery & Equipment",
    subtitle: "Crushing, screening, and milling plants from Henan Fote Heavy Machinery — distributed exclusively across Southern Africa.",
    cta: { label: "View Equipment", to: "/products/heavy-machinery" },
  },
  {
    image: "/images/hero/steel-welding.jpg",
    overline: "Steel Fabrication",
    title: "Precision-Engineered Steel Structures",
    subtitle: "End-to-end fabrication services for commercial, industrial, and mining facilities. From design to on-site erection.",
    cta: { label: "Explore Fabrication", to: "/steel-fabrication" },
  },
];

const bitumenProducts = [
  { title: "Emulsions", desc: "SS60, CRS 60, CRS 65, CRS 70, CMS 60", icon: <Drop size={24} weight="duotone" /> },
  { title: "Modified Emulsions", desc: "SC-E1, SC-E2", icon: <Drop size={24} weight="duotone" /> },
  { title: "Primes", desc: "Fastset Prime, MC 30", icon: <Drop size={24} weight="duotone" /> },
  { title: "Fast Coat", desc: "Pre Coating Fluid", icon: <Drop size={24} weight="duotone" /> },
  { title: "Penetration Grade", desc: "70/100, 50/70, 35/50 Bitumen", icon: <Drop size={24} weight="duotone" /> },
];

const stats = [
  { value: "14+", label: "Years Experience" },
  { value: "100%", label: "SANRAL Compliant" },
  { value: "3", label: "Core Divisions" },
  { value: "SA", label: "Nationwide Reach" },
];

export default function Home() {
  const [currentSlide, setCurrentSlide] = useState(0);
  const nextSlide = useCallback(() => setCurrentSlide(p => (p + 1) % slides.length), []);
  const prevSlide = () => setCurrentSlide(p => (p - 1 + slides.length) % slides.length);

  useEffect(() => {
    const timer = setInterval(nextSlide, 6000);
    return () => clearInterval(timer);
  }, [nextSlide]);

  return (
    <div>
      {/* HERO SLIDESHOW */}
      <section className="relative h-[85vh] min-h-[600px] overflow-hidden" data-testid="hero-section">
        <AnimatePresence mode="wait">
          <motion.div key={currentSlide} initial={{ opacity: 0, scale: 1.05 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 0.98 }} transition={{ duration: 0.8 }} className="absolute inset-0">
            <img src={slides[currentSlide].image} alt={slides[currentSlide].title} className="w-full h-full object-cover" />
            <div className="absolute inset-0 bg-gradient-to-r from-[#0B2D2D]/90 via-[#0B2D2D]/70 to-transparent" />
          </motion.div>
        </AnimatePresence>

        <div className="relative z-10 h-full flex items-center">
          <div className="max-w-[1400px] mx-auto px-6 lg:px-10 w-full">
            <AnimatePresence mode="wait">
              <motion.div key={currentSlide} initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -20 }} transition={{ duration: 0.5, delay: 0.2 }} className="max-w-2xl">
                <p className="font-mono text-xs font-bold uppercase tracking-[0.25em] text-[#A7D129] mb-4">{slides[currentSlide].overline}</p>
                <h1 className="font-heading font-black text-4xl md:text-6xl lg:text-7xl tracking-tight text-white leading-[1.05] mb-6">{slides[currentSlide].title}</h1>
                <p className="text-base md:text-lg text-white/70 leading-relaxed mb-8">{slides[currentSlide].subtitle}</p>
                <div className="flex flex-wrap gap-4">
                  <Link to={slides[currentSlide].cta.to} className="inline-flex items-center gap-2 bg-[#00A091] text-white font-bold uppercase tracking-wider text-xs px-8 py-4 rounded-sm hover:bg-[#A7D129] hover:text-[#0B2D2D] transition-all duration-300" data-testid="hero-cta-primary">
                    {slides[currentSlide].cta.label} <ArrowRight size={16} weight="bold" />
                  </Link>
                  <Link to="/contact" className="inline-flex items-center gap-2 border-2 border-white/40 text-white font-bold uppercase tracking-wider text-xs px-8 py-4 rounded-sm hover:bg-white hover:text-[#0B2D2D] transition-all duration-300" data-testid="hero-cta-contact">
                    Get In Touch
                  </Link>
                </div>
              </motion.div>
            </AnimatePresence>

            {/* Slide Controls */}
            <div className="absolute bottom-10 left-6 lg:left-10 flex items-center gap-4">
              <button onClick={prevSlide} className="w-10 h-10 border border-white/30 text-white flex items-center justify-center hover:bg-white hover:text-[#0B2D2D] transition-all rounded-sm" data-testid="slide-prev"><ArrowLeft size={16} weight="bold" /></button>
              <div className="flex gap-2">
                {slides.map((_, i) => (
                  <button key={i} onClick={() => setCurrentSlide(i)} className={`h-1.5 rounded-full transition-all duration-500 ${i === currentSlide ? "w-10 bg-[#A7D129]" : "w-4 bg-white/40 hover:bg-white/60"}`} data-testid={`slide-dot-${i}`} />
                ))}
              </div>
              <button onClick={nextSlide} className="w-10 h-10 border border-white/30 text-white flex items-center justify-center hover:bg-white hover:text-[#0B2D2D] transition-all rounded-sm" data-testid="slide-next"><ArrowRight size={16} weight="bold" /></button>
            </div>
          </div>
        </div>
      </section>

      {/* BITUMEN FOCUS - Main Business */}
      <section className="py-20 lg:py-28 bg-white" data-testid="bitumen-focus">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
            <motion.div className="lg:col-span-5" initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeLeft}>
              <p className="font-mono text-xs font-bold uppercase tracking-[0.2em] text-[#00A091] mb-3">Our Core Business</p>
              <h2 className="font-heading font-bold text-3xl md:text-5xl tracking-tight text-[#0B2D2D] mb-6">Bitumen &amp; Emulsion Solutions</h2>
              <p className="text-base text-[#0B2D2D]/70 leading-relaxed mb-6">
                As one of Southern Africa's specialist suppliers, Kwazulu Supplies distributes a comprehensive range of bituminous road binders, emulsions, primes, pre-coats, and modified binders. All products meet SANRAL and SABS quality standards.
              </p>
              <Link to="/products/bitumen-solutions" className="inline-flex items-center gap-2 bg-[#00A091] text-white font-bold uppercase tracking-wider text-xs px-6 py-3 rounded-sm hover:bg-[#0B2D2D] transition-all duration-300" data-testid="bitumen-cta">
                View All Bitumen Products <ArrowRight size={14} weight="bold" />
              </Link>
            </motion.div>
            <motion.div className="lg:col-span-7" initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger}>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                {bitumenProducts.map((p, i) => (
                  <motion.div key={i} variants={fadeUp} className="card-clean p-5 rounded-sm" data-testid={`bitumen-card-${i}`}>
                    <div className="text-[#00A091] mb-3">{p.icon}</div>
                    <h3 className="font-heading font-bold text-sm text-[#0B2D2D] mb-1">{p.title}</h3>
                    <p className="text-xs text-[#0B2D2D]/50">{p.desc}</p>
                  </motion.div>
                ))}
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* BITUMEN EQUIPMENT HIGHLIGHT */}
      <section className="py-20 lg:py-24 bg-[#E0F5F2]" data-testid="equipment-highlight">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <motion.div className="order-2 lg:order-1" initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeLeft}>
              <div className="grid grid-cols-2 gap-4">
                <div className="img-zoom rounded-sm overflow-hidden"><img src="/images/spraymaster/sm-pmb.jpg" alt="SprayMaster PMB Plant" className="w-full h-[200px] object-cover" loading="lazy" /></div>
                <div className="img-zoom rounded-sm overflow-hidden"><img src="/images/spraymaster/sm-bep.jpg" alt="SprayMaster Emulsion Plant" className="w-full h-[200px] object-cover" loading="lazy" /></div>
                <div className="img-zoom rounded-sm overflow-hidden col-span-2"><img src="/images/spraymaster/sm-bt.jpg" alt="SprayMaster Tanks" className="w-full h-[220px] object-cover" loading="lazy" /></div>
              </div>
            </motion.div>
            <motion.div className="order-1 lg:order-2" initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeRight}>
              <img src="/images/spraymaster/sm-logo.png" alt="SprayMaster" className="h-12 mb-6" />
              <h2 className="font-heading font-bold text-3xl md:text-4xl tracking-tight text-[#0B2D2D] mb-4">Professional Bitumen Equipment</h2>
              <p className="text-base text-[#0B2D2D]/70 leading-relaxed mb-6">
                The SprayMaster range offers complete bitumen handling and processing solutions — from polymer modified bitumen plants and emulsion plants to thermal oil heaters, tanks, and drum melting units.
              </p>
              <Link to="/products/bitumen-equipment" className="inline-flex items-center gap-2 bg-[#0B2D2D] text-white font-bold uppercase tracking-wider text-xs px-6 py-3 rounded-sm hover:bg-[#00A091] transition-all duration-300" data-testid="equipment-cta">
                View SprayMaster Range <ArrowRight size={14} weight="bold" />
              </Link>
            </motion.div>
          </div>
        </div>
      </section>

      {/* THREE PILLARS */}
      <section className="py-20 lg:py-28 bg-white" data-testid="pillars-section">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
          <motion.div className="text-center mb-16" initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp}>
            <p className="font-mono text-xs font-bold uppercase tracking-[0.2em] text-[#00A091] mb-3">Our Integrated Approach</p>
            <h2 className="font-heading font-bold text-3xl md:text-5xl tracking-tight text-[#0B2D2D]">Three Core Business Pillars</h2>
          </motion.div>
          <motion.div className="grid grid-cols-1 md:grid-cols-3 gap-8" initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger}>
            {[
              { icon: <Drop size={32} weight="duotone" />, title: "Bitumen & Emulsion Supply", desc: "High-grade binders for road construction. Emulsions, primes, penetration grade bitumen meeting SANRAL standards.", link: "/products/bitumen-solutions", img: "/images/bitumen-drums-multiple.jpeg" },
              { icon: <Truck size={32} weight="duotone" />, title: "Equipment Distribution", desc: "Authorized FTM distributor for Southern Africa. Crushing, screening, and milling plants plus SprayMaster bitumen equipment.", link: "/products/heavy-machinery", img: "/images/mobile-crusher-hero.jpg" },
              { icon: <Wrench size={32} weight="duotone" />, title: "Steel Fabrication", desc: "Precision-engineered structures for commercial, industrial, and mining. Design, fabrication, and on-site erection.", link: "/steel-fabrication", img: "/images/pillars/steel-fabrication.jpg" },
            ].map((p, i) => (
              <motion.div key={i} variants={fadeUp}>
                <Link to={p.link} className="block card-clean rounded-sm overflow-hidden group h-full" data-testid={`pillar-card-${i}`}>
                  <div className="img-zoom h-[220px]"><img src={p.img} alt={p.title} className="w-full h-full object-cover" loading="lazy" /></div>
                  <div className="p-6">
                    <div className="text-[#00A091] mb-3">{p.icon}</div>
                    <h3 className="font-heading font-bold text-xl text-[#0B2D2D] mb-3 group-hover:text-[#00A091] transition-colors">{p.title}</h3>
                    <p className="text-sm text-[#0B2D2D]/60 leading-relaxed mb-4">{p.desc}</p>
                    <span className="inline-flex items-center gap-1 text-[#00A091] font-bold text-sm group-hover:gap-2 transition-all">Explore <CaretRight size={14} weight="bold" /></span>
                  </div>
                </Link>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* STATS */}
      <section className="bg-[#0B2D2D] py-16" data-testid="stats-section">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {stats.map((s, i) => (
              <motion.div key={i} className="text-center" initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.1, duration: 0.5 }}>
                <p className="font-heading font-black text-4xl md:text-5xl text-[#A7D129] mb-2">{s.value}</p>
                <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#E0F5F2]/60">{s.label}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* VALUES */}
      <section className="py-20 lg:py-28 bg-[#E0F5F2]" data-testid="why-choose-section">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
          <motion.div className="text-center mb-16" initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp}>
            <p className="font-mono text-xs font-bold uppercase tracking-[0.2em] text-[#00A091] mb-3">Why KZNSC</p>
            <h2 className="font-heading font-bold text-3xl md:text-5xl tracking-tight text-[#0B2D2D]">Built on Excellence</h2>
          </motion.div>
          <motion.div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6" initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger}>
            {[
              { icon: <ShieldCheck size={28} weight="duotone" />, title: "Integrity", text: "Ethical conduct and transparency in every project and business relationship." },
              { icon: <Target size={28} weight="duotone" />, title: "Excellence", text: "Superior quality through world-class products and precision engineering." },
              { icon: <Lightning size={28} weight="duotone" />, title: "Synergy", text: "Integrated solutions combining equipment, materials, and fabrication." },
              { icon: <HardHat size={28} weight="duotone" />, title: "Safety", text: "Commitment to SABS and SANRAL standards in all operations." },
            ].map((v, i) => (
              <motion.div key={i} variants={fadeUp} className="bg-white p-6 rounded-sm hover-lift" data-testid={`value-card-${i}`}>
                <div className="w-12 h-12 bg-[#E0F5F2] rounded-sm flex items-center justify-center text-[#00A091] mb-4">{v.icon}</div>
                <h3 className="font-heading font-bold text-lg text-[#0B2D2D] mb-2">{v.title}</h3>
                <p className="text-sm text-[#0B2D2D]/60 leading-relaxed">{v.text}</p>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-[#00A091] py-20" data-testid="cta-section">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 text-center">
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6 }}>
            <h2 className="font-heading font-bold text-3xl md:text-5xl tracking-tight text-white mb-4">Ready to Build?</h2>
            <p className="text-white/70 text-base max-w-xl mx-auto mb-8">Contact us for quotes on bitumen products, equipment, or fabrication services.</p>
            <Link to="/contact" className="inline-flex items-center gap-2 bg-white text-[#0B2D2D] font-bold uppercase tracking-wider text-xs px-10 py-4 rounded-sm hover:bg-[#A7D129] transition-all duration-300" data-testid="cta-contact">
              Contact Us Today <ArrowRight size={16} weight="bold" />
            </Link>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
